import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import MessageModal from '../components/MessageModal';

const MyNetwork = () => {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('connections');
  const [connections, setConnections] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [networkInsights, setNetworkInsights] = useState({});
  const [networkAnalytics, setNetworkAnalytics] = useState(null);
  const [healthScore, setHealthScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedUserForMessage, setSelectedUserForMessage] = useState(null);
  const [connectionStatuses, setConnectionStatuses] = useState({});
  const [inviteForm, setInviteForm] = useState({
    invited_email: '',
    invited_name: '',
    message: ''
  });

  useEffect(() => {
    fetchNetworkData();
    fetchInvitations();
    fetchPendingRequests();
    fetchSentRequests();
  }, [user]);

  useEffect(() => {
    if (activeTab === 'insights' && user) {
      fetchNetworkAnalytics();
    }
  }, [activeTab, user]);

  const fetchNetworkData = async () => {
    if (!user) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Fetch network connections
      const networkResponse = await axios.get(`${backendUrl}/api/users/${user.id}/connections`);
      setConnections(networkResponse.data.mutual_connections || []);
      setNetworkInsights(networkResponse.data);
      
      // Fetch smart suggestions with scoring
      const suggestionsResponse = await axios.get(`${backendUrl}/api/connections/smart-suggestions?limit=10`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Format suggestions with match data
      const smartSuggestions = suggestionsResponse.data.suggestions || [];
      setSuggestions(smartSuggestions.map(s => ({
        ...s.user,
        match_score: s.match_score,
        match_reasons: s.reasons
      })));
      
    } catch (error) {
      console.error('Error fetching network data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingRequests = async () => {
    if (!user) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/connections/pending-requests`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPendingRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
    }
  };

  const fetchSentRequests = async () => {
    if (!user) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/connections/sent-requests`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSentRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching sent requests:', error);
    }
  };

  const fetchNetworkAnalytics = async () => {
    if (!user) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Fetch full analytics
      const analyticsResponse = await axios.get(`${backendUrl}/api/network/analytics`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setNetworkAnalytics(analyticsResponse.data.analytics);
      
      // Fetch health score breakdown
      const healthResponse = await axios.get(`${backendUrl}/api/network/health-score`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setHealthScore(healthResponse.data.health_score);
      
    } catch (error) {
      console.error('Error fetching network analytics:', error);
    }
  };

  const fetchInvitations = async () => {
    if (!user) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/invitations`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setInvitations(response.data || []);
    } catch (error) {
      console.error('Error fetching invitations:', error);
    }
  };

  const searchUsers = async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/search/users?query=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      console.log('🔍 Search results:', response.data);
      setSearchResults(response.data || []);
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const sendConnectionRequest = async (userId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.post(`${backendUrl}/api/users/${userId}/connection-request`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Update connection status
      setConnectionStatuses(prev => ({
        ...prev,
        [userId]: 'pending'
      }));
      
      // Remove from suggestions
      setSuggestions(suggestions.filter(user => user.id !== userId));
      
      // Refresh sent requests
      fetchSentRequests();
      
      alert('Connection request sent!');
    } catch (error) {
      console.error('Error sending connection request:', error);
      alert(error.response?.data?.detail || 'Error sending connection request');
    }
  };

  const acceptConnectionRequest = async (connectionId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await axios.put(`${backendUrl}/api/connections/${connectionId}/accept`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Refresh all network data
      fetchNetworkData();
      fetchPendingRequests();
      
      alert('Connection request accepted!');
    } catch (error) {
      console.error('Error accepting connection request:', error);
      alert(error.response?.data?.detail || 'Error accepting connection request');
    }
  };

  const rejectConnectionRequest = async (connectionId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await axios.put(`${backendUrl}/api/connections/${connectionId}/reject`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Refresh pending requests
      fetchPendingRequests();
      
      alert('Connection request rejected');
    } catch (error) {
      console.error('Error rejecting connection request:', error);
      alert(error.response?.data?.detail || 'Error rejecting connection request');
    }
  };

  const removeConnection = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this connection?')) {
      return;
    }
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // First, find the connection ID
      const findResponse = await axios.get(`${backendUrl}/api/connections/${userId}/find`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const connectionId = findResponse.data.connection_id;
      
      // Then delete it
      await axios.delete(`${backendUrl}/api/connections/${connectionId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Refresh connections
      fetchNetworkData();
      
      alert('Connection removed');
    } catch (error) {
      console.error('Error removing connection:', error);
      alert(error.response?.data?.detail || 'Error removing connection');
    }
  };

  const sendInvitation = async (e) => {
    e.preventDefault();
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await axios.post(`${backendUrl}/api/invitations`, inviteForm, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setShowInviteModal(false);
      setInviteForm({ invited_email: '', invited_name: '', message: '' });
      fetchInvitations(); // Refresh invitations
      alert('Invitation sent successfully!');
    } catch (error) {
      console.error('Error sending invitation:', error);
      alert(error.response?.data?.detail || 'Error sending invitation');
    }
  };

  // Debounced search
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (userSearchTerm) {
        searchUsers(userSearchTerm);
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [userSearchTerm]);

  const filteredConnections = connections.filter(connection =>
    connection.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    connection.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    connection.industry_tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const tabs = [
    { id: 'connections', label: 'My Connections', count: connections.length },
    { id: 'suggestions', label: 'Suggestions', count: suggestions.length },
    { id: 'search', label: 'Find People' },
    { id: 'invitations', label: 'Invitations', count: invitations.length },
    { id: 'pending', label: 'Pending', count: pendingRequests.length + sentRequests.length },
    { id: 'insights', label: 'Network Insights' }
  ];

  // Message modal functions
  const openMessageModal = (user) => {
    setSelectedUserForMessage(user);
    setShowMessageModal(true);
  };

  const closeMessageModal = () => {
    setShowMessageModal(false);
    setSelectedUserForMessage(null);
  };

  const handleMessageSuccess = (message) => {
    alert(message);
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-300 rounded w-1/4"></div>
          <div className="grid md:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm p-6">
                <div className="w-16 h-16 bg-gray-300 rounded-full mx-auto mb-4"></div>
                <div className="h-4 bg-gray-300 rounded w-3/4 mx-auto mb-2"></div>
                <div className="h-3 bg-gray-300 rounded w-1/2 mx-auto"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">My Network</h1>
            <p className="text-gray-600">Connect with fellow merchants and industry professionals</p>
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2"
          >
            ✉️ Invite People
          </button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder="Search connections..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500"
            />
            <span className="absolute left-4 top-3.5 text-gray-400">🔍</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                {tab.count !== undefined && (
                  <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                    activeTab === tab.id
                      ? 'bg-teal-100 text-teal-600'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'connections' && (
          <div>
            {filteredConnections.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg mb-4">No connections yet</p>
                <p className="text-gray-400">Start building your network by connecting with merchants in your industry</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredConnections.map((connection) => (
                  <div key={connection.id} className="bg-white rounded-lg shadow-sm p-6 text-center hover:shadow-md transition-shadow">
                    <div className="w-16 h-16 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">
                      {connection.full_name.charAt(0)}
                    </div>
                    
                    <h3 className="font-semibold text-gray-900 mb-1">{connection.full_name}</h3>
                    {connection.company_name && (
                      <p className="text-sm text-gray-600 mb-2">{connection.company_name}</p>
                    )}
                    <p className="text-sm text-gray-500 mb-4">{connection.location}</p>
                    
                    {connection.industry_tags && connection.industry_tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 justify-center mb-4">
                        {connection.industry_tags.slice(0, 2).map((tag, index) => (
                          <span key={index} className="bg-teal-50 text-teal-700 px-2 py-1 rounded-full text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => openMessageModal(connection)}
                        className="flex-1 bg-teal-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-teal-700 transition-colors"
                      >
                        💬 Message
                      </button>
                      <button
                        onClick={() => removeConnection(connection.id)}
                        className="flex-1 border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                    
                    <div className="mt-3 text-xs text-gray-500">
                      {connection.connections_count} mutual connections
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'suggestions' && (
          <div>
            {suggestions.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg mb-4">No suggestions available</p>
                <p className="text-gray-400">We'll suggest new connections based on your network and interests</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {suggestions.map((suggestion) => (
                  <div key={suggestion.id} className="bg-white rounded-lg shadow-sm p-6 text-center hover:shadow-md transition-shadow">
                    <div className="w-16 h-16 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">
                      {suggestion.full_name.charAt(0)}
                    </div>
                    
                    <h3 className="font-semibold text-gray-900 mb-1">{suggestion.full_name}</h3>
                    {suggestion.company_name && (
                      <p className="text-sm text-gray-600 mb-2">{suggestion.company_name}</p>
                    )}
                    <p className="text-sm text-gray-500 mb-4">{suggestion.location}</p>
                    
                    {suggestion.industry_tags && suggestion.industry_tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 justify-center mb-4">
                        {suggestion.industry_tags.slice(0, 2).map((tag, index) => (
                          <span key={index} className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {/* Match Reasons */}
                    {suggestion.match_reasons && suggestion.match_reasons.length > 0 && (
                      <div className="mb-4 space-y-1">
                        {suggestion.match_reasons.slice(0, 2).map((reason, index) => (
                          <div key={index} className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded flex items-center justify-center gap-1">
                            <span>{reason.icon}</span>
                            <span>{reason.details}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {connectionStatuses[suggestion.id]?.status === 'pending' ? (
                      <button
                        disabled
                        className="w-full bg-gray-300 text-gray-600 px-4 py-2 rounded-lg cursor-not-allowed"
                      >
                        ⏳ Request Pending
                      </button>
                    ) : (
                      <button
                        onClick={() => sendConnectionRequest(suggestion.id)}
                        className="w-full bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors"
                      >
                        🤝 Connect
                      </button>
                    )}
                    
                    {/* Match Score */}
                    {suggestion.match_score && (
                      <div className="mt-3 text-xs font-semibold text-teal-600">
                        {suggestion.match_score}% match
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'search' && (
          <div>
            <div className="mb-6">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search for users to connect with..."
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                />
                <span className="absolute left-4 top-3.5 text-gray-400">🔍</span>
              </div>
            </div>

            {searchLoading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-lg shadow-sm p-6">
                    <div className="w-16 h-16 bg-gray-300 rounded-full mx-auto mb-4 animate-pulse"></div>
                    <div className="h-4 bg-gray-300 rounded w-3/4 mx-auto mb-2 animate-pulse"></div>
                    <div className="h-3 bg-gray-300 rounded w-1/2 mx-auto animate-pulse"></div>
                  </div>
                ))}
              </div>
            ) : searchResults.length === 0 && userSearchTerm ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg mb-4">No users found</p>
                <p className="text-gray-400">Try searching with different keywords</p>
              </div>
            ) : searchResults.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg mb-4">Search for people to connect with</p>
                <p className="text-gray-400">Enter a name, company, or industry to find other merchants</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {searchResults.map((searchUser) => (
                  <div key={searchUser.id} className="bg-white rounded-lg shadow-sm p-6 text-center hover:shadow-md transition-shadow">
                    <div className="w-16 h-16 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">
                      {searchUser.full_name.charAt(0)}
                    </div>
                    
                    <h3 className="font-semibold text-gray-900 mb-1">{searchUser.full_name}</h3>
                    {searchUser.company_name && (
                      <p className="text-sm text-gray-600 mb-2">{searchUser.company_name}</p>
                    )}
                    <p className="text-sm text-gray-500 mb-4">{searchUser.location}</p>
                    
                    {searchUser.industry_tags && searchUser.industry_tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 justify-center mb-4">
                        {searchUser.industry_tags.slice(0, 2).map((tag, index) => (
                          <span key={index} className="bg-purple-50 text-purple-700 px-2 py-1 rounded-full text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {searchUser.id === user?.id ? (
                      <button
                        disabled
                        className="w-full bg-gray-200 text-gray-500 px-4 py-2 rounded-lg cursor-not-allowed"
                      >
                        You
                      </button>
                    ) : connectionStatuses[searchUser.id]?.status === 'accepted' ? (
                      <button
                        disabled
                        className="w-full bg-green-100 text-green-700 px-4 py-2 rounded-lg cursor-not-allowed"
                      >
                        ✓ Connected
                      </button>
                    ) : connectionStatuses[searchUser.id]?.status === 'pending' ? (
                      <button
                        disabled
                        className="w-full bg-gray-300 text-gray-600 px-4 py-2 rounded-lg cursor-not-allowed"
                      >
                        ⏳ Request Pending
                      </button>
                    ) : (
                      <button
                        onClick={() => sendConnectionRequest(searchUser.id)}
                        className="w-full bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors"
                      >
                        🤝 Connect
                      </button>
                    )}
                    
                    <div className="mt-3 text-xs text-gray-500">
                      {searchUser.connections_count || 0} connections
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'invitations' && (
          <div>
            {invitations.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg mb-4">No invitations sent</p>
                <p className="text-gray-400">Invite people to join the Labamu Hub</p>
              </div>
            ) : (
              <div className="space-y-4">
                {invitations.map((invitation) => (
                  <div key={invitation.id} className="bg-white rounded-lg shadow-sm p-6 flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {invitation.invited_name || invitation.invited_email}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">{invitation.invited_email}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>Status: {invitation.status}</span>
                        <span>Sent: {new Date(invitation.created_at).toLocaleDateString()}</span>
                        <span>Expires: {new Date(invitation.expires_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      invitation.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      invitation.status === 'accepted' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {invitation.status.charAt(0).toUpperCase() + invitation.status.slice(1)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'pending' && (
          <div>
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Incoming Requests</h2>
              {pendingRequests.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">No pending incoming requests</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingRequests.map((request) => (
                    <div key={request.connection_id} className="bg-white rounded-lg shadow-sm p-6 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                          {request.requester.full_name.charAt(0)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-1">{request.requester.full_name}</h3>
                          {request.requester.company_name && (
                            <p className="text-sm text-gray-600">{request.requester.company_name}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            Requested {new Date(request.requested_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => acceptConnectionRequest(request.connection_id)}
                          className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors"
                        >
                          ✓ Accept
                        </button>
                        <button
                          onClick={() => rejectConnectionRequest(request.connection_id)}
                          className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          ✗ Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Sent Requests</h2>
              {sentRequests.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">No pending outgoing requests</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {sentRequests.map((request) => (
                    <div key={request.connection_id} className="bg-white rounded-lg shadow-sm p-6 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                          {request.recipient.full_name.charAt(0)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-1">{request.recipient.full_name}</h3>
                          {request.recipient.company_name && (
                            <p className="text-sm text-gray-600">{request.recipient.company_name}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            Sent {new Date(request.requested_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm">
                          ⏳ Pending
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'insights' && (
          <div>
            {!networkAnalytics || !healthScore ? (
              <div className="text-center py-12">
                <div className="animate-pulse">
                  <div className="h-8 bg-gray-300 rounded w-1/4 mx-auto mb-4"></div>
                  <div className="h-4 bg-gray-300 rounded w-1/2 mx-auto"></div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Network Health Score */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">Network Health Score</h3>
                  
                  <div className="flex items-center justify-center mb-8">
                    <div className={`text-6xl font-bold ${
                      healthScore.health_level.color === 'green' ? 'text-green-600' :
                      healthScore.health_level.color === 'yellow' ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {healthScore.overall_score}
                      <span className="text-2xl">/100</span>
                    </div>
                  </div>
                  
                  <div className="text-center mb-6">
                    <span className={`inline-block px-4 py-2 rounded-full font-semibold ${
                      healthScore.health_level.color === 'green' ? 'bg-green-100 text-green-800' :
                      healthScore.health_level.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {healthScore.health_level.level}
                    </span>
                    <p className="text-gray-600 mt-2">{healthScore.health_level.message}</p>
                  </div>
                  
                  {/* Component Breakdown */}
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-700">Network Size</span>
                        <span className="font-semibold">{healthScore.components.size.score}/{healthScore.components.size.max}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-teal-600 h-2 rounded-full transition-all"
                          style={{ width: `${healthScore.components.size.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-700">Network Diversity</span>
                        <span className="font-semibold">{healthScore.components.diversity.score}/{healthScore.components.diversity.max}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{ width: `${healthScore.components.diversity.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-700">Activity Level</span>
                        <span className="font-semibold">{healthScore.components.activity.score}/{healthScore.components.activity.max}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full transition-all"
                          style={{ width: `${healthScore.components.activity.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-700">Network Growth</span>
                        <span className="font-semibold">{healthScore.components.growth.score}/{healthScore.components.growth.max}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-purple-600 h-2 rounded-full transition-all"
                          style={{ width: `${healthScore.components.growth.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Improvement Tips */}
                  {healthScore.tips && healthScore.tips.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-3">💡 Tips to Improve</h4>
                      <div className="space-y-2">
                        {healthScore.tips.map((tip, index) => (
                          <div key={index} className="text-sm">
                            <p className="text-blue-800 font-medium">{tip.tip}</p>
                            <p className="text-blue-600 text-xs">{tip.action}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Stats Cards */}
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                    <div className="w-12 h-12 bg-teal-100 text-teal-600 rounded-lg flex items-center justify-center mx-auto mb-4 text-2xl">
                      👥
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-2">{networkAnalytics.total_connections}</div>
                    <div className="text-gray-600">Total Connections</div>
                  </div>
                  
                  <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                    <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4 text-2xl">
                      🏭
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-2">{Object.keys(networkAnalytics.connections_by_industry || {}).length}</div>
                    <div className="text-gray-600">Industries</div>
                  </div>
                  
                  <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                    <div className="w-12 h-12 bg-green-100 text-green-600 rounded-lg flex items-center justify-center mx-auto mb-4 text-2xl">
                      📍
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-2">{Object.keys(networkAnalytics.connections_by_location || {}).length}</div>
                    <div className="text-gray-600">Locations</div>
                  </div>
                </div>
                
                {/* Connections by Industry */}
                {Object.keys(networkAnalytics.connections_by_industry || {}).length > 0 && (
                  <div className="bg-white rounded-lg shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">🏭 Connections by Industry</h3>
                    <div className="space-y-3">
                      {Object.entries(networkAnalytics.connections_by_industry).slice(0, 5).map(([industry, count]) => (
                        <div key={industry}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-700 capitalize">{industry}</span>
                            <span className="font-semibold text-teal-600">{count}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-teal-600 h-2 rounded-full"
                              style={{ width: `${(count / networkAnalytics.total_connections) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Connections by Location */}
                {Object.keys(networkAnalytics.connections_by_location || {}).length > 0 && (
                  <div className="bg-white rounded-lg shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">📍 Connections by Location</h3>
                    <div className="space-y-3">
                      {Object.entries(networkAnalytics.connections_by_location).slice(0, 5).map(([location, count]) => (
                        <div key={location}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-700">{location}</span>
                            <span className="font-semibold text-blue-600">{count}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${(count / networkAnalytics.total_connections) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Top Connectors */}
                {networkAnalytics.top_connectors && networkAnalytics.top_connectors.length > 0 && (
                  <div className="bg-white rounded-lg shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">🤝 Top Connectors</h3>
                    <div className="space-y-3">
                      {networkAnalytics.top_connectors.map((connector, index) => (
                        <div key={connector.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-blue-500 text-white rounded-full flex items-center justify-center font-bold">
                              {connector.full_name.charAt(0)}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{connector.full_name}</p>
                              {connector.company_name && (
                                <p className="text-xs text-gray-600">{connector.company_name}</p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-teal-600">{connector.mutual_count}</p>
                            <p className="text-xs text-gray-500">mutual connections</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Invitation Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Invite People to Join</h2>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <form onSubmit={sendInvitation}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={inviteForm.invited_email}
                    onChange={(e) => setInviteForm({ ...inviteForm, invited_email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Enter email address"
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={inviteForm.invited_name}
                    onChange={(e) => setInviteForm({ ...inviteForm, invited_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Enter full name"
                  />
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Personal Message (Optional)
                  </label>
                  <textarea
                    value={inviteForm.message}
                    onChange={(e) => setInviteForm({ ...inviteForm, message: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                    rows="3"
                    placeholder="Add a personal message to your invitation"
                  ></textarea>
                </div>
                
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                  >
                    Send Invitation
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal */}
      <MessageModal
        isOpen={showMessageModal}
        onClose={closeMessageModal}
        targetUser={selectedUserForMessage}
        onSuccess={handleMessageSuccess}
      />
    </>
  );
};

export default MyNetwork;